import { useQuery } from '@tanstack/react-query';
import { getStainTypeFilteredIds } from 'api/stainTypes';
import { find, map } from 'es-toolkit/compat';
import { useMemo } from 'react';
import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import { useCurrentLabId } from 'utils/useCurrentLab';

export const getStudyStainTypeIds = async ({
  labId,
  studyId,
  signal,
}: {
  labId: string;
  studyId?: string;
  signal?: AbortSignal;
}) => {
  const paramsForStain = studyId ? { labId, filters: { studyId } } : { labId };
  return getStainTypeFilteredIds(paramsForStain, signal);
};

export const useStudyStainTypeIds = (
  studyId: string,
  {
    enabled = true,
  }: {
    enabled?: boolean;
  } = {}
) => {
  const { labId } = useCurrentLabId();

  const query = useQuery(
    ['stainsTypes', { studyId, labId }],
    ({ signal }) => getStudyStainTypeIds({ labId, studyId, signal }),
    { enabled }
  );

  return query;
};

export const useStudyStainTypes = (
  studyId: string,
  {
    enabled = true,
  }: {
    enabled?: boolean;
  } = {}
) => {
  const query = useStudyStainTypeIds(studyId, { enabled });

  const { stainTypeOptions, isLoadingStainTypeOptions } = useStainTypeOptions();

  const stainTypeIds = query.data;

  const stainTypes = useMemo(() => {
    return map(stainTypeIds, (stainTypeId) => find(stainTypeOptions, (stainType) => stainType.id === stainTypeId));
  }, [stainTypeIds, stainTypeOptions]);

  return { ...query, stainTypes, isLoadingStainTypeOptions };
};
